import { useRouter, useSearchParams } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAccount } from '@/domain/hooks/useAccount'
import { useDynamicContext } from '@dynamic-labs/sdk-react-core'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useMarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { getChainConfigEntry } from '@/config/chain'
import { assert } from '@/utils/assert'
import { BaseUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { useEffect, useMemo, useState } from 'react'
import { PageState } from '@/features/dialogs/common/types'
import { toBigInt } from '@/utils/bigNumber'
import { useTokensInfo } from '@/domain/wallet/useTokens/useTokensInfo'
import { getSwapFormValidator, SwapFormSchema } from './validators'
import {
  getDefaultFormValues,
  getSwapMode,
  getSwappableAssets,
  normalizeFormValues,
  swapTokens,
  useDebouncedDialogFormValues,
  useSwapFormFields,
} from './utils'
import { UseSwapResults } from './types'
import { wrapUnwrap } from './wrapUnrwap'
import { useGetPrice } from './priceQuery'
import { swap } from './swap'
import { useGetSwapTokens } from './tokensQuery'
import { OracleType } from '@/domain/wallet/useTokens/types'
import { tokensQueryKey } from '@/domain/wallet/useTokens/query'
import { queryClient } from '@/config/query-client'
import { Token } from '@/domain/types/Token'
import { marketBalancesQueryKey } from '@/domain/wallet/marketBalances'
import { useSideDrawer } from '@/domain/hooks/useSideDrawer'

interface UseSwapProps {
  isModal?: boolean
  filterZeroBalance?: boolean
  toTokens?: Token[]
}

export function useSwap({ isModal, filterZeroBalance = false, toTokens }: UseSwapProps): UseSwapResults {
  const router = useRouter()

  const { chainId } = usePageChainId()
  const { tokens } = useGetSwapTokens()
  const account = useAccount()
  const guestMode = !account
  const { open } = useSideDrawer()
  const { marketInfo } = useMarketInfo({ chainId })
  const { markets } = getChainConfigEntry(chainId)
  const { nativeAssetInfo } = markets ?? {}
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const { primaryWallet } = useDynamicContext()
  const [slippage, setSlippage] = useState(Percentage(0.01))
  const [swapState, setSwapState] = useState('')
  const [swapError, setSwapError] = useState(false)
  const [surgeAcknowledged, setSurgeAcknowledged] = useState(false)
  const [quoteError, setQuoteError] = useState(false)

  const searchParams = useSearchParams()
  const paramFromToken = searchParams.get('from')
  const paramToToken = searchParams.get('to')

  assert(nativeAssetInfo, 'Native asset info is required for swap')
  const { nativeAssetSymbol, wrappedNativeAssetSymbol } = nativeAssetInfo

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const formattedApiTokens = useMemo(() => {
    return tokens
      .filter((t) => t.token.symbol !== wrappedNativeAssetSymbol && t.token.symbol !== nativeAssetSymbol)
      .map((t) => ({
        ...t.token,
        oracleType: 'zero-price' as OracleType,
      }))
  }, [wrappedNativeAssetSymbol, nativeAssetSymbol])

  const { tokensInfo } = useTokensInfo({
    tokens: formattedApiTokens,
    chainId,
  })
  const routerTokens = tokensInfo.all()
  const walletInfo = useMarketWalletInfo({ chainId })

  function resetErrors(): void {
    setSwapError(false)
    setQuoteError(false)
  }

  const swappableAssets = getSwappableAssets(
    [marketInfo.findOneReserveBySymbol(nativeAssetInfo.nativeAssetSymbol), ...marketInfo.reserves],
    walletInfo,
    routerTokens,
  )

  const form = useForm<SwapFormSchema>({
    resolver: zodResolver(getSwapFormValidator({ walletInfo, marketInfo, routerTokens })),
    defaultValues: getDefaultFormValues(swappableAssets, {
      paramFromToken,
      paramToToken,
      toTokens,
    }),
    mode: 'onChange',
  })

  const rawFormValues = form.watch()
  const formValues = normalizeFormValues(rawFormValues, swappableAssets)
  const swapMode = getSwapMode(form, nativeAssetSymbol, wrappedNativeAssetSymbol)

  const { debouncedFormValues, isDebouncing, isFormValid } = useDebouncedDialogFormValues({
    form,
    swappableAssets,
  })

  function createCustomOnChange(fieldName: 'from' | 'to') {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      form.setValue(`${fieldName}.value`, value)
      form.trigger().catch(console.error)
      resetErrors()
    }
  }

  const fromTokenField = useSwapFormFields({
    form,
    assets: swappableAssets,
    fieldName: 'from',
    counterpartFieldName: 'to',
    marketInfo,
    isOnSwapPage: !isModal,
    filterZeroBalance,
    toTokens,
    setSurgeAcknowledged,
    onChange: createCustomOnChange('from'),
    resetErrors,
  })

  const toTokenField = useSwapFormFields({
    form,
    assets: swappableAssets,
    fieldName: 'to',
    counterpartFieldName: 'from',
    marketInfo,
    isOnSwapPage: !isModal,
    filterZeroBalance,
    toTokens,
    setSurgeAcknowledged,
    onChange: createCustomOnChange('to'),
    resetErrors,
  })

  function handleSwapTokens(): void {
    setSurgeAcknowledged(false)
    if (!isModal) {
      router.replace(`/swap?from=${form.getValues('to.symbol')}&to=${form.getValues('from.symbol')}`, {
        scroll: false,
      })
    }
    swapTokens(form)
    resetErrors()
  }

  function handleWrapUnwrap(): Promise<void> {
    return wrapUnwrap({
      primaryWallet,
      chainId,
      value: toBigInt(formValues.from.token.toBaseUnit(formValues.from.value)),
      swapMode,
      setSwapState,
      setSwapError,
      setPageStatus,
    })
  }

  const {
    price: priceData,
    isLoading: priceLoading,
    isRefetching: priceRefetching,
  } = useGetPrice({
    inputToken: debouncedFormValues.from.token.address,
    outputToken: debouncedFormValues.to.token.address,
    account,
    inputAmount: debouncedFormValues.from.token.toBaseUnit(debouncedFormValues.from.value),
    enabled:
      debouncedFormValues.from.value.gt(0) &&
      !swapState &&
      swapMode === 'swap' &&
      !guestMode &&
      !quoteError &&
      isFormValid,
    resetErrors,
  })

  function handleSwap(): Promise<void> {
    return swap({
      inputToken: debouncedFormValues.from.token,
      outputToken: debouncedFormValues.to.token,
      inputAmount: debouncedFormValues.from.token.toBaseUnit(debouncedFormValues.from.value),
      priceData,
      primaryWallet,
      chainId,
      surgeAcknowledged,
      nativeAssetSymbol,
      setSwapState,
      setSwapError,
      setQuoteError,
      onSuccess: () => {
        setPageStatus('success')
        setSurgeAcknowledged(false)
        void queryClient.invalidateQueries({
          queryKey: tokensQueryKey({ tokens: formattedApiTokens, chainId, account }),
        })
        void queryClient.invalidateQueries({
          queryKey: marketBalancesQueryKey({ account, chainId }),
        })
      },
    })
  }

  const quoteOutputAmount = priceData && 'result' in priceData ? priceData.result.effectiveOutputAmount : null

  useEffect(() => {
    if (swapMode !== 'swap') {
      form.setValue('to.value', form.getValues('from.value'))
      return
    }

    if (!debouncedFormValues.from.value.gt(0) || !debouncedFormValues.to.token || !quoteOutputAmount) {
      form.setValue('to.value', '')
      return
    }

    const toAmount = debouncedFormValues.to.token.fromBaseUnit(BaseUnitNumber(quoteOutputAmount))
    form.setValue('to.value', toAmount.toString())
  }, [
    swapMode,
    debouncedFormValues.from.value,
    quoteOutputAmount,
    debouncedFormValues.to.token,
    form.setValue,
    form.getValues,
  ])

  const priceUpdating = priceLoading || priceRefetching

  return {
    form,
    swapMode,
    swapState,
    swapError,
    quoteError,
    fromTokenField,
    toTokenField,
    priceData,
    priceUpdating,
    fromToken: formValues.from,
    toToken: formValues.to,
    slippage,
    surgeAcknowledged,
    setSurgeAcknowledged,
    setSlippage,
    wrapUnwrap: handleWrapUnwrap,
    swap: handleSwap,
    pageStatus: {
      state: pageStatus,
      actionsEnabled:
        debouncedFormValues.from.value.gt(0) &&
        debouncedFormValues.to.value.gt(0) &&
        isFormValid &&
        !isDebouncing &&
        !priceUpdating,
      goToSuccessScreen: () => setPageStatus('success'),
      resetForm: () => {
        const defaultValues = getDefaultFormValues(swappableAssets, {
          paramFromToken,
          paramToToken,
          toTokens,
        })
        setPageStatus('form')
        form.reset(defaultValues)
      },
    },
    guestMode,
    swapTokens: handleSwapTokens,
    openConnectModal: open,
  }
}
